// The path a campaign walks from draft to serving, as one ordered list.
//
// It replaces a static paragraph that said the same thing for every campaign in
// every state ("Launch locks the validated plan in D1…"). The cost of that was
// concrete: on 2026-08-17 a campaign reported *"Waiting on: audience revision 1
// is not fully synced to meta (+1 more)"* and answering "what is that, and how
// long?" took a database session.
//
// **Nothing here is invented.** Every step's state is read from the preflight
// checks or from `campaign_provider_stage_journal`; where the data cannot say,
// the step says so rather than guessing. This module deliberately replaces the
// repo's previous attempt at this screen: `flows/launch-sequences.ts` and
// `flows/launch-progress.tsx`, a design prototype whose steps were fabricated —
// invented endpoints, fake request ids, fake log lines — and which was never
// mounted and could not have been, because none of its steps corresponded to
// anything the product does. Both were deleted alongside this file's arrival;
// only their stylesheet survived, and it is what `components/publish-sequence`
// renders on.
//
// It is a module, not inline JSX, so every test can reach it. See
// `lib/creative-sync-view.ts` for the incident that established the rule.

import type {
  CampaignPreflight,
  CampaignPreflightCheck,
  CampaignProviderState,
  CampaignStageEstimate,
  CampaignSyncAd,
  CampaignSyncDeliveryGroup,
  CampaignSyncStage,
  CampaignSyncStatus,
  CampaignSyncTarget,
} from '@/api/manage-types';
import type { FailureView } from '@/api/types.gen';
import { campaignPublishOutcome } from './campaign-sync-outcome';

/**
 * `blocked` is distinct from `failed` on purpose: a blocked step is waiting on
 * work elsewhere (an audience still matching at Meta) and needs patience, while
 * a failed step needs a person. Rendering them alike is what made "waiting on
 * audience revision 1" read as an error.
 */
export type PublishStepState = 'done' | 'active' | 'blocked' | 'failed' | 'pending' | 'note';

export interface PublishStepChild {
  label: string;
  state: PublishStepState;
  detail?: string;
  /** `detail`'s failure in words, when the journal row carried one. */
  failureView?: FailureView;
  providerObjectId?: string;
}

export interface PublishStep {
  key: string;
  label: string;
  state: PublishStepState;
  /** Free text under the label — the reason, never a restatement of the state. */
  detail?: string;
  /** Rolled-up progress, e.g. ad sets 1 of 2. */
  count?: { done: number; total: number };
  /** The individual provider objects behind a roll-up, revealed on expand. */
  children?: PublishStepChild[];
  /** Epoch ms; the first attempt ever, present once the step has started. */
  startedAt?: number;
  /**
   * Epoch ms; when the attempt currently running started.
   *
   * Differs from `startedAt` only after a retry, and that difference is the
   * point: the journal preserves `created_at` across retries, so a live clock
   * anchored there reports the time since the *first* attempt.
   */
  attemptStartedAt?: number;
  /** Some leg of this step has been attempted more than once. */
  retried?: boolean;
  completedAt?: number;
  /** Epoch ms of a recorded failure, which ends an attempt without completing it. */
  failedAt?: number;
  /** Public failure code or bounded provider message. */
  failure?: string;
  /**
   * `failure` in words (`shared::failure_copy`, the server's `failure` beside
   * `last_error`). Absent on older responses and on the strings this module
   * makes up itself; the component then shows `failure` as it always did.
   */
  failureView?: FailureView;
  /** Ids worth copying: provider object ids, so an operator can leave for Ads Manager. */
  evidence?: { label: string; value: string }[];
  /** Absent unless the server had ≥5 single-attempt samples for this stage. */
  estimate?: { p50Ms: number; p90Ms: number; samples: number };
}

/**
 * Steps whose truth comes from the stage journal rather than the preflight.
 *
 * The distinction matters for failure attribution: a publish-time failure must
 * never be pinned on a pre-launch step, which describes the operator's plan
 * rather than the worker's run.
 */
/**
 * `serving` is deliberately absent, and it used to be here.
 *
 * Serving is not journal-backed — its state comes from the sync block's
 * `provider_state`, and it is never the step a publish was interrupted *on*,
 * because activation is the one thing that had not been attempted yet. Leaving
 * it in the candidate set meant a single failed `execution` row painted a
 * second step red: the worker records `publish: failed` and then
 * `execution: failed`, so `Published` was already failed and the attribution
 * pass moved on to the first `pending` journal step, which was `Serving`.
 */
const JOURNAL_BACKED = new Set([
  'campaign',
  'ad_sets',
  'ads',
  'budgets',
  'readback',
  'published',
]);

const PROVIDER_STATE_DETAIL: Record<CampaignProviderState, string> = {
  active: 'delivering',
  active_pending_delivery: 'live at the provider; first impressions not counted yet',
  scheduled_paused: 'built and paused — no flight covers today',
  paused_superseded: 'paused; a newer revision has taken over',
  disabled: 'disabled at the provider',
  disabled_superseded: 'disabled; a newer revision has taken over',
};

/**
 * Which provider publishes this channel.
 *
 * Meta is its own destination; CTV and Display both go to TTD. The rule was
 * hand-written as `channel === 'meta' ? 'meta' : 'ttd'` in four places across
 * two screens before this — harmless while there are two providers, and a
 * four-site edit the day there are three.
 */
export function deliveryTargetFor(channel: string | null | undefined): CampaignSyncTarget {
  return channel === 'meta' ? 'meta' : 'ttd';
}

function ms(iso?: string): number | undefined {
  if (!iso) return undefined;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? undefined : value;
}

function stageState(stage: CampaignSyncStage): PublishStepState {
  if (stage.status === 'complete') return 'done';
  if (stage.status === 'failed') return 'failed';
  if (stage.status === 'running') return 'active';
  return 'pending';
}

/** The worst state in a group, since a roll-up is only as good as its weakest child. */
function worst(states: PublishStepState[]): PublishStepState {
  if (states.some((s) => s === 'failed')) return 'failed';
  if (states.some((s) => s === 'active')) return 'active';
  if (states.some((s) => s === 'pending')) return 'pending';
  if (states.some((s) => s === 'blocked')) return 'blocked';
  // `note` is a finding that gates nothing, so a group of notes is done.
  return states.length > 0 ? 'done' : 'pending';
}

function checksFor(preflight: CampaignPreflight | undefined, codes: string[]): CampaignPreflightCheck[] {
  const wanted = new Set(codes);
  return (preflight?.checks ?? []).filter((check) => wanted.has(check.code));
}

/**
 * A preflight-backed step.
 *
 * `blocked`, never `failed` — a preflight check that has not passed describes
 * work still to happen, not a fault. It is `pending` when the preflight has not
 * been read at all, because "we do not know" and "nothing is wrong" are
 * different and the panel must not conflate them.
 */
function preflightStep(
  key: string,
  label: string,
  preflight: CampaignPreflight | undefined,
  codes: string[],
): PublishStep {
  const checks = checksFor(preflight, codes);
  if (!preflight) return { key, label, state: 'pending' };
  if (checks.length === 0) {
    // The check does not apply to this campaign (provider config checks are
    // target-specific), so there is nothing outstanding.
    return { key, label, state: 'done' };
  }
  const failing = checks.filter((check) => check.status === 'fail');
  return {
    key,
    label,
    state: failing.length === 0 ? 'done' : 'blocked',
    count: { done: checks.length - failing.length, total: checks.length },
    detail: failing[0]?.message,
    children: checks.map((check) => ({
      label: check.message,
      // `info` is a real finding on a launched campaign that names no remedy
      // (the plan is frozen). Rendering it as `blocked` — "waiting" — sent the
      // operator looking for something to do about it.
      state: check.status === 'pass'
        ? ('done' as const)
        : check.status === 'info'
          ? ('note' as const)
          : ('blocked' as const),
    })),
  };
}

/**
 * The stage keys each publish-time lane writes for one leg.
 *
 * The first publish journals `ad_{segment}_{creative}` and
 * `audience_{segment}`; every later lane journals under its own operation id
 * so it never disturbs the publish record: `addition_{id}_ad_{segment}`,
 * `rollout_{id}_ad_{segment}`, `slot_{id}_ad_{segment}`, and the `_group_`
 * forms for TTD. Matching only the `ad_` prefix is what read "2 of 4" on a
 * live campaign whose second creative had been added after publish.
 */
const LATER_LANES = /^(?:addition|rollout|slot)_.+_(ad|group)_(\d+)$/;

/** The segment (and, for a first-publish ad, the creative) a stage key names. */
export function parseAdStageKey(
  stageKey: string,
): { segment: number; creative?: number; lane: 'publish' | 'addition' | 'rollout' | 'slot' } | null {
  const first = /^ad_(\d+)_(\d+)$/.exec(stageKey);
  if (first) return { segment: Number(first[1]), creative: Number(first[2]), lane: 'publish' };
  const later = LATER_LANES.exec(stageKey);
  if (later && later[1] === 'ad') {
    return { segment: Number(later[2]), lane: stageKey.split('_')[0] as 'addition' | 'rollout' | 'slot' };
  }
  return null;
}

/** The segment a group stage key names, for the first publish or a later lane. */
export function parseGroupStageKey(
  stageKey: string,
): { segment: number; lane: 'publish' | 'addition' | 'rollout' | 'slot' } | null {
  const first = /^audience_(\d+)$/.exec(stageKey);
  if (first) return { segment: Number(first[1]), lane: 'publish' };
  const later = LATER_LANES.exec(stageKey);
  if (later && later[1] === 'group') {
    return { segment: Number(later[2]), lane: stageKey.split('_')[0] as 'addition' | 'rollout' | 'slot' };
  }
  return null;
}

const LANE_WORD = { publish: '', addition: ' · adding a creative', rollout: ' · roll-out', slot: ' · pause or resume' } as const;

function adStageLabel(stageKey: string, creativeName: (position: number) => string): string {
  const parsed = parseAdStageKey(stageKey);
  if (!parsed) return stageKey;
  const creative = parsed.creative === undefined ? '' : ` · ${creativeName(parsed.creative)}`;
  return `Audience ${parsed.segment + 1}${creative}${LANE_WORD[parsed.lane]}`;
}

function groupStageLabel(stageKey: string): string {
  const parsed = parseGroupStageKey(stageKey);
  if (!parsed) return stageKey;
  return `Audience ${parsed.segment + 1}${LANE_WORD[parsed.lane]}`;
}

/**
 * A leg the plan is flying, read from `platform_ads` / `platform_delivery_groups`
 * rather than from the journal. The journal records what each lane did; these
 * tables record what exists, binding repairs and later additions included.
 */
interface TruthLeg {
  segment: number;
  creative?: number;
  label: string;
  state: PublishStepState;
  providerObjectId?: string;
  detail?: string;
  /** When the provider object was last written, ms; a failure older than this is history. */
  updatedAt?: number;
}

/**
 * Whether a provider status means the object exists at the provider. Every
 * lane writes its own vocabulary (`active`, `activating`, `paused`, `PAUSED`,
 * `associated`, `idle`, `planned`); only `planned` means "not created yet".
 */
export function providerObjectExists(status: string | null | undefined, providerId: string | null | undefined): boolean {
  if (!providerId) return false;
  return (status ?? '').toLowerCase() !== 'planned';
}

export type ProviderStatusClass = 'active' | 'paused' | 'attention' | 'absent';

const ACTIVE_STATUSES = new Set(['active', 'activating', 'associated', 'converged', 'enabled']);
const PAUSED_STATUSES = new Set(['disabled', 'disabled_superseded', 'campaign_paused', 'adset_paused']);

/**
 * What a provider status says the object is doing, in one of four words.
 *
 * Every lane writes its own vocabulary and Meta's `effective_status` has a
 * dozen values, and until 2026-09-18 the summaries counted anything that
 * existed as live — a PAUSED ad set read "1 live" and a disapproved ad read
 * "active". `attention` is everything the object could be doing that is
 * neither delivering nor deliberately stopped: in review, disapproved, with
 * issues, idle, or a status this code has never seen.
 */
export function providerStatusClass(
  status: string | null | undefined,
  providerId: string | null | undefined,
): ProviderStatusClass {
  if (!providerObjectExists(status, providerId)) return 'absent';
  const value = (status ?? '').toLowerCase();
  if (ACTIVE_STATUSES.has(value)) return 'active';
  if (value.startsWith('paused') || PAUSED_STATUSES.has(value)) return 'paused';
  return 'attention';
}

function adLegs(ads: CampaignSyncAd[], target: CampaignSyncTarget, creativeName: (position: number) => string): TruthLeg[] {
  return ads
    .filter((ad) => ad.target === target)
    .map((ad) => ({
      segment: ad.audience_segment_position,
      creative: ad.creative_position,
      label: `Audience ${ad.audience_segment_position + 1} · ${creativeName(ad.creative_position)}`,
      state: providerObjectExists(ad.provider_status, ad.provider_ad_id)
        ? 'done'
        : ad.last_error
          ? 'failed'
          : 'pending',
      providerObjectId: ad.provider_ad_id,
      detail: providerObjectExists(ad.provider_status, ad.provider_ad_id) ? undefined : ad.last_error ?? undefined,
      updatedAt: ms(ad.updated_at),
    }));
}

function groupLegs(groups: CampaignSyncDeliveryGroup[], target: CampaignSyncTarget): TruthLeg[] {
  return groups
    .filter((group) => group.target === target)
    .map((group) => ({
      segment: group.audience_segment_position,
      label: `Audience ${group.audience_segment_position + 1}`,
      state: providerObjectExists(group.provider_status, group.provider_delivery_group_id)
        ? 'done'
        : group.last_error
          ? 'failed'
          : 'pending',
      providerObjectId: group.provider_delivery_group_id ?? undefined,
      detail: providerObjectExists(group.provider_status, group.provider_delivery_group_id) ? undefined : group.last_error ?? undefined,
      updatedAt: ms(group.updated_at),
    }));
}

/**
 * A journal-backed step, rolled up over every stage whose key matches.
 *
 * `expectedTotal` is the number of legs the *plan* calls for, and passing it is
 * what stops the denominator lying. The worker writes one journal row per leg
 * immediately before that leg's provider call, so a roll-up counted from rows
 * alone counts only the legs that have started: a two-audience Meta publish
 * reads "0 of 1", and in the gap between one `stage_complete` and the next
 * `stage_running` it reads "1 of 1" with a green check — a step claiming to be
 * finished with half its work unwritten. That gap is normally milliseconds,
 * but it is also exactly where a lost execution lease lands the publish, and
 * then the false "1 of 1 ✓" is permanent.
 */
function journalStep(
  key: string,
  label: string,
  stages: CampaignSyncStage[],
  matches: (stageKey: string) => boolean,
  childLabel: (stage: CampaignSyncStage) => string,
  expectedTotal?: number,
  /**
   * What the plan is flying, from `platform_ads` / `platform_delivery_groups`.
   * When present it is the list the count is drawn from; journal rows then
   * contribute only in-flight state (`running`, `failed`) and timing.
   */
  truth?: { legs: TruthLeg[]; legOf: (stageKey: string) => { segment: number; creative?: number } | null },
  /** When the `publish` span closed, if it has: the roll-up's own end. */
  publishCompletedAt?: number,
): PublishStep | null {
  const mine = stages.filter((stage) => matches(stage.stage_key));
  const legs = truth?.legs ?? [];
  if (mine.length === 0 && legs.length === 0) return null;
  let children: PublishStepChild[];
  // How many children the count is drawn from: the legs when the truth list is
  // present, else every row. In-flight rows of a later lane are shown beside
  // the legs but never counted as legs — "Ads created 4 of 6" would be a
  // roll-out and a pause masquerading as two more ads.
  let counted: number;
  // Failed rows the legs have outlived; they name no current failure.
  const dropped = new Set<CampaignSyncStage>();
  if (legs.length > 0 && truth) {
    // The truth list, with any journal row still running or failed laid over
    // the leg it names. A completed journal row says nothing the truth list
    // does not already say — and its provider id may be the one a binding
    // repair has since replaced.
    children = legs.map((leg) => ({
      label: leg.label,
      state: leg.state,
      detail: leg.detail,
      providerObjectId: leg.providerObjectId,
    }));
    counted = children.length;
    for (const stage of mine) {
      if (stage.status !== 'running' && stage.status !== 'failed') continue;
      const named = truth.legOf(stage.stage_key);
      // Strict on the creative: a later-lane key (`addition_*_ad_N`) names a
      // segment and no creative, so it matches no ad leg and becomes its own
      // child; before this it landed on the segment's first creative, and a
      // failed addition of the second creative read as the first one failing.
      // A group key names its segment's one group leg, which is the match.
      const index = named
        ? legs.findIndex((leg) => leg.segment === named.segment && leg.creative === named.creative)
        : -1;
      // A failure the provider objects have outlived. The leg it names — or,
      // for a later-lane row, every leg of its segment — was written after
      // the row failed, so a retry or a binding repair has replaced what the
      // failure describes, and showing it would poison a settled log.
      if (stage.status === 'failed' && named) {
        const failedAt = ms(stage.updated_at);
        const outlivedBy = index >= 0
          ? [legs[index]!]
          : legs.filter((leg) => leg.segment === named.segment);
        const newest = outlivedBy.reduce<number | undefined>(
          (best, leg) => (leg.updatedAt !== undefined && (best === undefined || leg.updatedAt > best) ? leg.updatedAt : best),
          undefined,
        );
        if (failedAt !== undefined && newest !== undefined && failedAt < newest) {
          dropped.add(stage);
          continue;
        }
      }
      const overlay = {
        state: stageState(stage),
        detail: stage.last_error ?? undefined,
        failureView: stage.failure,
      };
      if (index >= 0) {
        children[index] = { ...children[index]!, ...overlay };
      } else {
        children.push({ label: childLabel(stage), providerObjectId: stage.provider_object_id, ...overlay });
      }
    }
  } else {
    children = mine.map((stage) => ({
      label: childLabel(stage),
      state: stageState(stage),
      detail: stage.last_error ?? undefined,
      failureView: stage.failure,
      providerObjectId: stage.provider_object_id,
    }));
    counted = children.length;
  }
  const total = Math.max(counted, expectedTotal ?? 0);
  // Legs the plan calls for that have no row yet. Rendered rather than merely
  // counted, so expanding a roll-up shows the work still to come instead of
  // implying the list is complete.
  for (let index = counted; index < total; index += 1) {
    children.push({ label: 'not started', state: 'pending', detail: undefined, providerObjectId: undefined });
  }
  const done = children.slice(0, counted).filter((child) => child.state === 'done').length;
  const failed = mine.find((stage) => stage.status === 'failed' && !dropped.has(stage));
  const starts = mine.map((stage) => ms(stage.created_at)).filter((v): v is number => v !== undefined);
  const ends = mine.map((stage) => ms(stage.completed_at)).filter((v): v is number => v !== undefined);
  const settled = done === total && total > 0;
  // Only once every expected leg has landed. `ends.length === mine.length`
  // alone was true the moment the started legs finished, which is what froze
  // the elapsed clock on a half-built hierarchy. Once the publish span has
  // closed it is the end of every roll-up inside it: later lanes journal under
  // their own keys, so the last journal row is not the last thing that happened.
  const completedAt = !settled
    ? undefined
    : publishCompletedAt !== undefined
      ? publishCompletedAt
      : ends.length === mine.length && ends.length > 0
        ? Math.max(...ends)
        : undefined;
  const failingChild = children.find((child) => child.state === 'failed');
  return {
    key,
    label,
    state: rollupState(children.map((child) => child.state), done, total),
    count: { done, total },
    startedAt: starts.length > 0 ? Math.min(...starts) : undefined,
    attemptStartedAt: attemptStart(mine),
    retried: mine.some((stage) => (stage.attempts ?? 1) > 1),
    completedAt,
    failedAt: ms(failed?.updated_at),
    failure: failed?.last_error ?? failingChild?.detail,
    failureView: failed ? failed.failure : failingChild?.failureView,
    children,
    evidence: children
      .filter((child) => child.providerObjectId)
      .map((child) => ({ label: child.label, value: child.providerObjectId as string })),
  };
}

/**
 * The state of a roll-up, which is not quite the worst of its children.
 *
 * Partially-done is `active`, not `pending`: with one leg complete and one not
 * yet started, every child is either `done` or `pending`, and `worst` would
 * call the whole step pending — reading as "nothing has happened here" on a
 * step that is visibly half finished.
 */
function rollupState(states: PublishStepState[], done: number, total: number): PublishStepState {
  const base = worst(states);
  if (base === 'pending' && done > 0 && done < total) return 'active';
  return base;
}

/**
 * When the *current* attempt started, for stages that are running.
 *
 * `created_at` is the first attempt ever, and `stage_running`'s upsert leaves
 * it alone on retry while moving `updated_at` — so a stage retried twelve
 * hours after it first failed would otherwise render "12h00m" the instant it
 * resumed. `updated_at` on a running row is that row's current start.
 */
function attemptStart(stages: CampaignSyncStage[]): number | undefined {
  const running = stages
    .filter((stage) => stage.status === 'running')
    .map((stage) => ms(stage.updated_at))
    .filter((value): value is number => value !== undefined);
  return running.length > 0 ? Math.min(...running) : undefined;
}

export function campaignPublishSequence(input: {
  target: CampaignSyncTarget;
  preflight?: CampaignPreflight;
  sync?: CampaignSyncStatus;
  /**
   * How many legs the plan calls for, so a roll-up can count work that has not
   * started. Optional: without it the counts fall back to rows-that-exist,
   * which is the old behaviour and still better than nothing.
   */
  expected?: { adSets?: number; ads?: number };
  /** The creative at each plan position, for the ad rows' labels. */
  creativeName?: (position: number) => string;
}): PublishStep[] {
  const { target, preflight, sync, expected } = input;
  const creativeName = input.creativeName ?? ((position: number) => `creative ${position + 1}`);
  const stages = (sync?.stages ?? []).filter((stage) => stage.target === target);
  const steps: PublishStep[] = [];
  const publishSpan = stages.find((stage) => stage.stage_key === 'publish');
  const publishCompletedAt = publishSpan?.status === 'complete' ? ms(publishSpan.completed_at) : undefined;

  // ── Before Launch: what the preflight is asserting ──────────────────────
  steps.push(
    preflightStep('plan', 'Plan validated', preflight, [
      'plan_integrity',
      'production',
      'audience_seed_revisions',
      'meta_custom_conversion',
      'ttd_conversion_reporting',
      'ttd_seed',
      'ttd_geo_targeting',
      'ttd_deal_targeting',
      'ttd_roi_goal',
      'ttd_ctv_bids',
      'ttd_display_bids',
      'meta_minimum_budget',
      'ttd_minimum_budget',
      'delivery_group',
      'delivery_group_count',
    ]),
  );
  steps.push(
    preflightStep('creatives', 'Creatives published', preflight, [
      'creatives',
      'creative',
      'creative_delivery',
      'meta_official_previews',
      'meta_placement_coverage',
      // A pinned revision whose call to action Graph has not accepted at
      // create_ad (a "Book Now" pinned before 2026-10-02). It blocks here,
      // with the creative, not later as a failed `ad_*` stage.
      'meta_creative_cta',
    ]),
  );
  steps.push(
    preflightStep('audiences', 'Audiences synced', preflight, [
      'audience',
      'audience_revision',
      'audience_delivery',
    ]),
  );

  // ── After Launch: what the journal recorded ─────────────────────────────
  const campaign = journalStep('campaign', 'Campaign created', stages, (k) => k === 'campaign', () => 'campaign');
  if (campaign) steps.push(campaign);

  const groupTruth = sync?.delivery_groups
    ? { legs: groupLegs(sync.delivery_groups, target), legOf: parseGroupStageKey }
    : undefined;
  const groups = journalStep(
    'ad_sets',
    target === 'meta' ? 'Ad sets created' : 'Ad groups created',
    stages,
    (k) => parseGroupStageKey(k) !== null,
    (stage) => groupStageLabel(stage.stage_key),
    expected?.adSets,
    groupTruth,
    publishCompletedAt,
  );
  if (groups) steps.push(groups);

  // Meta only. TTD attaches creatives when it creates the ad group, so there is
  // no per-ad object and therefore no step — absent, not empty.
  const adTruth = sync?.ads
    ? { legs: adLegs(sync.ads, target, creativeName), legOf: parseAdStageKey }
    : undefined;
  const ads = target === 'meta'
    ? journalStep(
      'ads',
      'Ads created',
      stages,
      (k) => parseAdStageKey(k) !== null,
      (stage) => adStageLabel(stage.stage_key, creativeName),
      expected?.ads,
      adTruth,
      publishCompletedAt,
    )
    : null;
  if (ads) steps.push(ads);

  // TTD only.
  const budgets = journalStep(
    'budgets',
    'Budget allocated',
    stages,
    (k) => k === 'budget_allocations',
    () => 'allocation',
  );
  if (budgets) steps.push(budgets);

  const readback = journalStep(
    'readback',
    'Hierarchy verified',
    stages,
    (k) => k === 'readback_paused' || k === 'readback_disabled',
    () => 'readback',
  );
  if (readback) steps.push(readback);

  // `publish` brackets the whole target publish: the worker opens it before any
  // provider call and closes it after the last one. So it is a span, not a
  // later sibling — and rendering its raw `running` status here made the list
  // show two things happening at once ("Ad sets created" active AND "Published"
  // active), with the Published clock re-counting every child's time.
  //
  // It stays in the list because "did the whole publish land" is a real
  // question, but while a child is still in flight it reads as `pending`: the
  // work it brackets is not finished, and its own start time is not shown until
  // it is the thing being waited on.
  const publish = stages.find((stage) => stage.stage_key === 'publish');
  if (publish) {
    const childActive = steps.some(
      (step) => JOURNAL_BACKED.has(step.key) && (step.state === 'active' || step.state === 'pending'),
    );
    const wrapped = publish.status === 'running' && childActive;
    steps.push({
      key: 'published',
      label: 'Published',
      state: wrapped ? 'pending' : stageState(publish),
      startedAt: wrapped ? undefined : ms(publish.created_at),
      attemptStartedAt: wrapped ? undefined : attemptStart([publish]),
      retried: publish.attempts > 1,
      completedAt: ms(publish.completed_at),
      failedAt: publish.status === 'failed' ? ms(publish.updated_at) : undefined,
      failure: publish.last_error ?? undefined,
      failureView: publish.failure,
    });
  }

  steps.push(servingStep(sync, target, publish, stages));

  // A failed `execution` stage means the publish was interrupted before it
  // could record which step it was on. Attach it to the step that was in
  // flight, so the failure is visible somewhere rather than nowhere.
  //
  // **Only to a publish-time step.** Searching all steps would land it on the
  // first unfinished one, which for a campaign whose preflight has not been
  // read is "Plan validated" — blaming the operator's plan for a queue lease
  // the worker lost. Preference is `active` over `pending`, because the step
  // that was running is the one that was interrupted.
  const execution = stages.find((stage) => stage.stage_key === 'execution' && stage.status === 'failed');
  // Old journal rows can predate the worker's successful-publish cleanup.
  // A later completed publish for this target and revision supersedes that
  // interruption. Compare the LAST failure transition, not its first attempt;
  // a newer failure must survive an older success. Missing/equal times prove
  // no ordering. An active provider state alone proves no publish recovery.
  const executionFailedAt = ms(execution?.updated_at);
  const publishedAt = publish?.status === 'complete' ? ms(publish.completed_at) : undefined;
  const executionRecovered = executionFailedAt !== undefined
    && publishedAt !== undefined
    && publishedAt > executionFailedAt;
  const alreadyNamed = steps.some(
    (step) => JOURNAL_BACKED.has(step.key) && step.state === 'failed',
  );
  // Nothing to attribute when the journal already said where it broke. The
  // worker records `publish: failed` alongside `execution: failed`, so without
  // this the message landed on a *second* step as well — and since Serving was
  // in the candidate set, a campaign that never reached activation reported
  // both "Published failed" and "Serving failed".
  if (execution && !executionRecovered && !alreadyNamed) {
    const publishTime = steps.filter((step) => JOURNAL_BACKED.has(step.key));
    const victim =
      publishTime.find((step) => step.state === 'active') ??
      publishTime.find((step) => step.state === 'pending');
    if (victim) {
      victim.state = 'failed';
      victim.failedAt = executionFailedAt;
      victim.failure = execution.last_error ?? 'publish execution failed';
      victim.failureView = execution.failure;
    } else {
      // The lease can be lost before any provider stage exists, so there may be
      // no journal-backed step at all. Say so on its own row rather than
      // borrowing one that describes work nobody attempted.
      steps.push({
        key: 'interrupted',
        label: 'Publish interrupted',
        state: 'failed',
        failure: execution.last_error ?? 'publish execution failed',
        failureView: execution.failure,
        startedAt: ms(execution.created_at),
        completedAt: ms(execution.completed_at),
        failedAt: executionFailedAt,
        retried: execution.attempts > 1,
      });
    }
  }

  return attachEstimates(steps, target, sync?.estimates ?? [], stages);
}

/**
 * Serving is its own step, and it may legitimately never be reached.
 *
 * A Meta publish can complete successfully without serving: if no flight covers
 * today, `flight_reconcile` finishes with `scheduled_paused` and every provider
 * object is built and paused. That is a success, and showing it as one — with
 * the reason — is the difference between "we are done" and "ads are running",
 * which the old paragraph could not express at all.
 */
function servingStep(
  sync: CampaignSyncStatus | undefined,
  target: CampaignSyncTarget,
  publish: CampaignSyncStage | undefined,
  stages: CampaignSyncStage[],
): PublishStep {
  // Meta activates inside `flight_reconcile`; TTD has an explicit `activate`.
  const activator = stages.find(
    (stage) => stage.stage_key === 'activate' || stage.stage_key === 'flight_reconcile',
  );
  const block = sync ? sync[target] : undefined;
  const providerState = block?.provider_state;
  const detail = providerState ? PROVIDER_STATE_DETAIL[providerState] : undefined;

  // The campaign-level verdict comes first, because `provider_state` is
  // written only by the paths that succeeded and is never cleared when one
  // fails. A block that failed carrying a stale `active` would otherwise
  // render the whole sequence as delivering.
  //
  // `campaignPublishOutcome` is the same function the publish modal reads, on
  // purpose: this step and that modal describe one campaign, and when they had
  // separate derivations they disagreed about failure, wrong-destination and
  // local-ready — three states the sequence simply could not show.
  if (sync) {
    const outcome = campaignPublishOutcome(sync, target);
    // `local_ready` means no sync rows at all. Journal rows contradict that:
    // something ran, so the absent block is missing evidence rather than a
    // deliberate dark launch, and calling it "done" would be the one reading
    // an operator must never be given.
    if (outcome === 'local_ready' && stages.length === 0) {
      return {
        key: 'serving',
        label: 'Serving',
        state: 'done',
        detail: 'provider publishing is off — the validated plan is held here, not sent to the provider',
      };
    }
    if (outcome === 'failed') {
      return {
        key: 'serving',
        label: 'Serving',
        state: 'failed',
        detail,
        failure:
          block?.last_error ??
          (block === null
            ? `this campaign published to the other destination, not ${target}`
            : 'the provider sync failed'),
        failureView: block?.last_error ? block.failure : undefined,
      };
    }
    // `pending` means the publish has not reached a verdict, so a
    // `provider_state` left over from an earlier revision says nothing.
    if (outcome === 'pending') {
      return { key: 'serving', label: 'Serving', state: 'pending' };
    }
  }

  if (providerState === 'active') {
    return {
      key: 'serving',
      label: 'Serving',
      state: 'done',
      detail,
      startedAt: ms(activator?.created_at),
      completedAt: ms(activator?.completed_at),
    };
  }
  if (providerState === 'active_pending_delivery') {
    return { key: 'serving', label: 'Serving', state: 'active', detail, startedAt: ms(activator?.created_at) };
  }
  if (providerState) {
    // Paused for a stated reason. Pending, not failed — nothing is wrong.
    return { key: 'serving', label: 'Serving', state: 'pending', detail };
  }
  return {
    key: 'serving',
    label: 'Serving',
    state: 'pending',
    detail: publish?.status === 'complete' ? 'waiting for the provider to report delivery' : undefined,
  };
}

/**
 * Hang historical durations off the steps that have them.
 *
 * Only steps 4+ can ever carry one: the stage journal is campaign-only, and
 * `sync_state` keeps a single overwritten `updated_at`, so a finished audience
 * or creative sync leaves no duration behind at all. The pre-launch steps show
 * elapsed and nothing else, and the UI must not imply otherwise.
 */
function attachEstimates(
  steps: PublishStep[],
  target: CampaignSyncTarget,
  estimates: CampaignStageEstimate[],
  stages: CampaignSyncStage[],
): PublishStep[] {
  if (estimates.length === 0) return steps;
  const byStage = new Map(
    estimates.filter((e) => e.target === target).map((e) => [e.stage_key, e]),
  );
  const stageKeysFor = (step: PublishStep): string[] => {
    switch (step.key) {
      case 'campaign':
        return ['campaign'];
      case 'ad_sets':
        return stages.map((s) => s.stage_key).filter((k) => parseGroupStageKey(k) !== null);
      case 'ads':
        return stages.map((s) => s.stage_key).filter((k) => parseAdStageKey(k) !== null);
      case 'budgets':
        return ['budget_allocations'];
      case 'readback':
        return ['readback_paused', 'readback_disabled'];
      // `published` deliberately has none. Its stage brackets every other
      // stage, so estimating it alongside its own children would show the same
      // seconds twice — once on the step that spent them and once on the span
      // that contains it.
      default:
        return [];
    }
  };
  return steps.map((step) => {
    const relevant = stageKeysFor(step)
      .map((key) => byStage.get(key))
      .filter((e): e is CampaignStageEstimate => e !== undefined);
    // Every stage the roll-up covers must have history, or the number would
    // describe a subset while being labelled as the whole. One estimated leg
    // out of four is not "typically 4s" for the step — and the module's
    // contract is already that an absent estimate means "not enough history",
    // which the UI renders correctly as nothing at all.
    const wanted = stageKeysFor(step);
    if (relevant.length === 0 || relevant.length !== wanted.length) return step;
    // A roll-up's stages run in sequence, so their typical durations add.
    const p50Ms = relevant.reduce((sum, e) => sum + e.p50_ms, 0);
    const p90Ms = relevant.reduce((sum, e) => sum + e.p90_ms, 0);
    return {
      ...step,
      estimate: { p50Ms, p90Ms, samples: Math.min(...relevant.map((e) => e.samples)) },
    };
  });
}

/**
 * `4m12s`, `28s`, `1h04m`. Undefined when the step has not started.
 *
 * A running step is timed from the *current* attempt, not the first one. The
 * journal keeps `created_at` across retries, so anchoring the live clock there
 * made a stage retried the next morning open at "14h20m" — a number that
 * describes how long the operator had been asleep, not how long anything was
 * taking.
 */
export function formatElapsed(step: PublishStep, nowMs: number): string | undefined {
  // Failed journal rows deliberately have no completed_at. Their last failure
  // transition freezes the clock; without a known end, omit the duration.
  // Treating them as running made a recovered campaign show an 824-hour failure.
  const endedAt = step.completedAt ?? step.failedAt;
  if (step.state === 'failed' && endedAt === undefined) return undefined;
  const running = endedAt === undefined;
  const start = running ? step.attemptStartedAt ?? step.startedAt : step.startedAt;
  if (start === undefined) return undefined;
  const end = endedAt ?? nowMs;
  const elapsed = formatDuration(Math.max(0, end - start));
  // A settled step that took more than one attempt has no single duration:
  // `completedAt - startedAt` spans the gaps between them. Say so rather than
  // presenting the span as a measurement.
  return !running && step.retried ? `${elapsed} across retries` : elapsed;
}

export function formatDuration(totalMs: number): string {
  const seconds = Math.floor(totalMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m${String(seconds % 60).padStart(2, '0')}s`;
  return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}m`;
}

/** `typically 6-9m (n=7)`, or nothing at all when there is no evidence. */
export function formatEstimate(step: PublishStep): string | undefined {
  if (!step.estimate) return undefined;
  const { p50Ms, p90Ms, samples } = step.estimate;
  const low = formatDuration(p50Ms);
  const high = formatDuration(p90Ms);
  const range = low === high ? low : `${low}-${high}`;
  return `typically ${range} (n=${samples})`;
}
